/**
 * ETConfigurator — step-based configurator wizard (FAT-291).
 *
 * Extends PostSelection (assets/post-selection.js, must be loaded first) and
 * reuses its ATC trigger handling, variant detection, price totals and
 * cart/add.js flow. This class only adds the wizard layer: step navigation,
 * skip links, the step indicator and the accordion groups on the last step.
 */
class ETConfigurator extends PostSelection {
  constructor(product, defaultVariant) {
    super(product, defaultVariant, { modalId: "modal-configurator" });

    this.cfgModal = document.getElementById("modal-configurator");
    this.stepsEl = this.cfgModal.querySelector(".et-cfg__steps");
    this.stepPanels = [...this.cfgModal.querySelectorAll("[data-cfg-step]")];
    this.indicatorBtns = [...this.cfgModal.querySelectorAll("[data-cfg-ind]")];
    this.nextBtn = this.cfgModal.querySelector(".et-cfg__next");
    this.skipBtn = this.cfgModal.querySelector(".et-cfg__skip");
    this.wizardBackBtn = this.cfgModal.querySelector(".et-cfg__back");
    this.progressCurrentEl = this.cfgModal.querySelector("[data-cfg-current]");

    this.currentStep = 0;
    this._wizardEvents();
    this._renderWizardStep();
  }

  get _lastStep() {
    return this.stepPanels.length - 1;
  }

  _wizardEvents() {
    if (this.nextBtn) {
      this.nextBtn.addEventListener("click", () => {
        this._goToStep(this.currentStep + 1);
      });
    }

    if (this.skipBtn) {
      this.skipBtn.addEventListener("click", () => {
        if (this.currentStep >= this._lastStep) {
          this._handleCheckoutBtn();
        } else {
          this._goToStep(this.currentStep + 1);
        }
      });
    }

    if (this.wizardBackBtn) {
      this.wizardBackBtn.addEventListener("click", () => {
        this._goToStep(this.currentStep - 1);
      });
    }

    // Completed indicator steps are clickable to navigate back
    this.indicatorBtns.forEach((btn) => {
      btn.addEventListener("click", () => {
        const target = +btn.dataset.cfgInd;
        if (target < this.currentStep) {
          this._goToStep(target);
        }
      });
    });

    // Accordion groups (step 3)
    this.cfgModal.querySelectorAll("[data-cfg-group-toggle]").forEach((toggle) => {
      toggle.addEventListener("click", () => {
        const group = toggle.closest("[data-cfg-group]");
        const isOpen = group.classList.contains("is-open");

        group
          .closest(".et-cfg__groups")
          .querySelectorAll("[data-cfg-group].is-open")
          .forEach((openGroup) => this._toggleGroup(openGroup, false));

        this._toggleGroup(group, !isOpen);
      });
    });

    // Item selection → refresh totals (PostSelection binds nothing on these
    // inputs because they don't use the .post-selection__item wrapper class)
    this.stepsEl.querySelectorAll(".et-cfg__item-input").forEach((input) => {
      input.addEventListener("change", () => {
        this._updatePrices();
      });
    });
  }

  _toggleGroup(group, open) {
    const toggle = group.querySelector("[data-cfg-group-toggle]");
    const body = group.querySelector(".et-cfg__group-body");
    group.classList.toggle("is-open", open);
    if (toggle) toggle.setAttribute("aria-expanded", open ? "true" : "false");
    if (body) body.hidden = !open;
  }

  _goToStep(index) {
    const clamped = Math.max(0, Math.min(index, this._lastStep));
    this.currentStep = clamped;
    this._renderWizardStep();

    if (this.stepsEl) {
      this.stepsEl.scrollTop = 0;
    }
  }

  _renderWizardStep() {
    const isLast = this.currentStep >= this._lastStep;

    this.stepPanels.forEach((panel, i) => {
      panel.hidden = i !== this.currentStep;
    });

    this.indicatorBtns.forEach((btn, i) => {
      btn.classList.toggle("is-done", i < this.currentStep);
      btn.classList.toggle("is-active", i === this.currentStep);
      btn.disabled = i >= this.currentStep;
    });

    if (this.progressCurrentEl) {
      this.progressCurrentEl.textContent = this.currentStep + 1;
    }

    if (this.wizardBackBtn) {
      this.wizardBackBtn.hidden = this.currentStep === 0;
    }

    if (this.nextBtn) this.nextBtn.hidden = isLast;
    // Inline display, because PostSelection._updateSingleStepButtons() sets
    // style.display = "flex" on the checkout button, which beats [hidden]
    if (this.checkoutBtn) {
      this.checkoutBtn.hidden = !isLast;
      this.checkoutBtn.style.display = isLast ? "flex" : "none";
    }

    if (this.skipBtn) {
      const skipNext = this.skipBtn.querySelector("[data-cfg-skip-next]");
      const skipCart = this.skipBtn.querySelector("[data-cfg-skip-cart]");
      if (skipNext) skipNext.hidden = isLast;
      if (skipCart) skipCart.hidden = !isLast;
    }
  }

  _handleATCBtn() {
    this._goToStep(0);
    super._handleATCBtn();
  }

  // Wizard items live in .et-cfg__steps instead of the flat modal's
  // .post-selection__items-container, so scope the checked-items query there.
  _getUpsellItems() {
    if (!this.stepsEl) return [];

    const items = this.stepsEl.querySelectorAll('input[type="checkbox"]:checked');

    return Array.from(items).map((item) => ({
      id: +item.value,
      quantity: 1,
      title: item.dataset.title,
      img: item.dataset.img,
      price: item.dataset.price,
      warranty: item.dataset.warrantyPrice === "true",
      // FAT-327 — the parent's _getUpsellPayload stamps this onto the line as
      // `_delivery_time`; without it wizard upsells reached the order with no
      // delivery time while flat-configurator upsells had one.
      deliveryTime: item.dataset.deliveryTime || "",
    }));
  }
}
